import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Copy, Check, Zap } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { accountKey } from './accountsFormat';
import './AccountsPyramidTab.css';

/*
 * AccountsPyramidTab — read-only v1 of the GAS ValuePyramidPanel for
 * /accounts: 4-tier value pyramid (corporate strategy → critical
 * requirements) with curated content per tier and the golden-thread
 * ladder + talk track when threads exist. Editing/regeneration stay in
 * GAS for now. Data: GET /api/v1/value-pyramids/:account?scope=account
 * → { exists, pyramid }.
 */

const VP_TIERS = [
  { key: 'corporate_strategy', label: 'Corporate strategy', topW: 34, botW: 52 },
  { key: 'business_initiatives', label: 'Business initiatives', topW: 52, botW: 70 },
  { key: 'it_initiatives', label: 'Challenges & IT', topW: 70, botW: 86 },
  { key: 'critical_requirements', label: 'Critical requirements', topW: 86, botW: 100 },
];
const VP_FIELD_BY_TIER = {
  critical_requirements: 'requirement_id',
  it_initiatives: 'it_initiative_id',
  business_initiatives: 'business_initiative_id',
  corporate_strategy: 'corporate_strategy_id',
};

const pct = (c) => `${Math.round((Number(c) || 0) * 100)}%`;
const confClass = (c) => {
  const v = Number(c);
  if (!Number.isFinite(v)) return '';
  if (v >= 0.75) return 'vp-conf--high';
  if (v >= 0.5) return 'vp-conf--mid';
  return 'vp-conf--low';
};

function Evidence({ ev }) {
  if (!Array.isArray(ev) || ev.length === 0) return null;
  return (
    <span className="vp-ev">
      {ev.map((e, i) => {
        const ref = String(e.source_ref || '');
        const isUrl = /^https?:\/\//i.test(ref);
        return (
          <span className="vp-ev-chip" key={i} title={e.quote || ''}>
            <span className="vp-ev-type">{e.source_type}</span>
            {isUrl
              ? <a href={ref} target="_blank" rel="noreferrer">{ref.replace(/^https?:\/\//, '').slice(0, 36)}</a>
              : <span>{ref.slice(0, 40)}</span>}
          </span>
        );
      })}
    </span>
  );
}

function TierItems({ items }) {
  if (items.length === 0) {
    return <p className="accounts-panel-note">No grounded items — treated honestly rather than invented.</p>;
  }
  return (
    <div className="vp-items">
      {items.map((it) => (
        <div className="vp-item" key={it.id}>
          <div className="vp-item-text">
            {it.text}
            {it.curated && <span className="vp-tag">curated</span>}
            {it.added && <span className="vp-tag">added</span>}
          </div>
          {it.rationale && <div className="vp-item-rationale">{it.rationale}</div>}
          <div className="vp-item-foot">
            <span className={`vp-conf ${confClass(it.confidence)}`}>
              {it.confidence != null ? pct(it.confidence) : '—'}
            </span>
            <Evidence ev={it.evidence} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ThreadLadder({ thread, tiers, onCopy, copied }) {
  return (
    <div className="vp-ladder">
      {VP_TIERS.map((t) => {
        const id = thread[VP_FIELD_BY_TIER[t.key]];
        const item = id && (tiers[t.key] || []).find((x) => x.id === id);
        return (
          <div className={`vp-rung${item ? '' : ' vp-rung--missing'}`} key={t.key}>
            <div className="vp-rung-tier">{t.label}</div>
            <div className="vp-rung-text">{item ? item.text : 'not linked'}</div>
          </div>
        );
      })}
      {thread.talk_track && (
        <div className="vp-talk">
          <div className="ts-section-label">Talk track{thread.curated && <span className="vp-tag">curated</span>}</div>
          <p className="vp-talk-text">{thread.talk_track}</p>
          <button type="button" className="vp-copy-btn" onClick={() => onCopy(thread.talk_track)}>
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy talk track'}
          </button>
        </div>
      )}
      {thread.messaging_narrative && (
        <details className="vp-narrative">
          <summary>Why this ladders</summary>
          <p>{thread.messaging_narrative}</p>
        </details>
      )}
    </div>
  );
}

export default function AccountsPyramidTab({ selectedDepartment }) {
  const account = accountKey(selectedDepartment);
  const [openTier, setOpenTier] = useState(null);
  const [selThread, setSelThread] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-value-pyramid', account],
    queryFn: () => apiClient.get(`/api/v1/value-pyramids/${encodeURIComponent(account)}`, {
      params: { scope: 'account' },
    }),
    enabled: !!account,
    staleTime: 60_000,
  });

  if (isLoading) return <div className="accounts-panel-note">Loading value pyramid…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load value pyramid'}
      </div>
    );
  }
  if (!data?.exists || !data.pyramid) {
    return (
      <div className="ts-card">
        <p className="accounts-panel-note">
          No value pyramid generated for this agency yet. Generation is owner-run
          (no in-app AI spend) — it will appear here once the owner batch covers this account.
        </p>
      </div>
    );
  }

  const pyr = data.pyramid;
  const tiers = pyr.tiers || {};
  const threads = pyr.threads || [];
  const activeThread = threads.find((t) => t.id === selThread) || null;
  const summary = pyr.assessment?.summary || '';
  const thinTiers = pyr.assessment?.thin_tiers || [];

  const copy = (text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }, () => {});
    }
  };

  return (
    <div className="accounts-pyramid">
      <div className="vp-meta">
        <span className={`vp-badge ${pyr.status === 'curated' ? 'vp-badge--curated' : 'vp-badge--ai'}`}>
          {pyr.status === 'curated' ? 'Curated' : 'AI draft'}
        </span>
        {pyr.stale && <span className="vp-badge vp-badge--stale" title="Source data changed since generation">stale</span>}
        <span className="vp-meta-note">
          {pct(pyr.assessment?.overall_confidence)} confidence · {pyr.model || 'n/a'} · {pyr.generatedAt ? String(pyr.generatedAt).slice(0, 10) : 'n/a'} · read-only (edit in the GAS dashboard)
        </span>
      </div>
      {summary && <p className="vp-summary">{summary}</p>}

      {threads.length > 0 && (
        <div className="vp-threads" role="group" aria-label="Golden thread">
          <span className="vp-threads-label"><Zap size={13} /> Golden thread</span>
          <button
            type="button"
            className={`vp-thread-pill${!selThread ? ' is-active' : ''}`}
            onClick={() => setSelThread(null)}
          >
            Overview
          </button>
          {threads.map((t) => (
            <button
              type="button"
              key={t.id}
              className={`vp-thread-pill${selThread === t.id ? ' is-active' : ''}`}
              onClick={() => setSelThread(t.id === selThread ? null : t.id)}
            >
              {t.panw_opportunity}
            </button>
          ))}
        </div>
      )}

      <div className="vp-stage">
        <div className="vp-canvas">
          {VP_TIERS.map((tier) => {
            const items = tiers[tier.key] || [];
            const clip = `polygon(${(100 - tier.topW) / 2}% 0, ${100 - (100 - tier.topW) / 2}% 0, ${100 - (100 - tier.botW) / 2}% 100%, ${(100 - tier.botW) / 2}% 100%)`;
            const inThread = activeThread && activeThread[VP_FIELD_BY_TIER[tier.key]];
            const open = openTier === tier.key;
            return (
              <React.Fragment key={tier.key}>
                <button
                  type="button"
                  className={`vp-band${open ? ' is-open' : ''}${activeThread && !inThread ? ' is-dim' : ''}`}
                  style={{ clipPath: clip, WebkitClipPath: clip }}
                  onClick={() => setOpenTier(open ? null : tier.key)}
                  aria-expanded={open}
                >
                  <span className="vp-band-label">{tier.label}</span>
                  <span className="vp-band-count">{items.length}</span>
                  {thinTiers.includes(tier.key) && <span className="vp-thin">thin</span>}
                </button>
                {open && (
                  <div className="vp-tier-detail">
                    <TierItems items={items} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="vp-rail">
          {activeThread ? (
            <>
              <div className="vp-rail-head">
                <span className="vp-rail-title">{activeThread.panw_opportunity}</span>
                <span className={`vp-conf ${confClass(activeThread.confidence)}`}>{pct(activeThread.confidence)}</span>
              </div>
              <ThreadLadder thread={activeThread} tiers={tiers} onCopy={copy} copied={copied} />
            </>
          ) : (
            <>
              <div className="ts-section-label">Palo Alto opportunities</div>
              <div className="vp-opps">
                {(pyr.panwOpportunities || []).map((o, i) => (
                  <div className="vp-opp" key={i} title={o.rationale || ''}>
                    <span className="vp-opp-name">{o.area}</span>
                    <span className="vp-opp-bar">
                      <span className="vp-opp-fill" style={{ width: pct(o.relevance) }} />
                    </span>
                    <span className="vp-opp-pct">{pct(o.relevance)}</span>
                  </div>
                ))}
                {(!pyr.panwOpportunities || pyr.panwOpportunities.length === 0) && (
                  <p className="accounts-panel-note">No mapped opportunities.</p>
                )}
              </div>
              {threads.length > 0 && (
                <p className="vp-rail-hint">Pick a golden-thread opportunity above to trace its ladder up the pyramid.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
